import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ArticleNumberInput } from '@/components/ui/article-number-input';
import { Badge, type BadgeProps } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useActivities } from '@/hooks/use-activities';
import { useInsurances } from '@/hooks/use-insurances';
import { useProject } from '@/hooks/use-projects';
import {
  useCancelRegistration,
  useChangeParticipantCount,
  useCreateBalanceInvoice,
  useGroupRegistrations,
  useMarkInvoiceManuallyResolved,
  useMergeRegistration,
  useReactivateRegistration,
  useRegenerateDepositInvoices,
  useRegistration,
  useRegistrationInvoicePreview,
  useRetryDepositInvoice,
  useUpdateRegistration,
} from '@/hooks/use-registrations';
import { hasRegistrationFieldOverride, restoreRegistrationFieldOverride } from '@/lib/registration-overrides';
import { formatCurrency, formatDateTime, getEffectiveRegistrationField, getLocalizedText } from '@/lib/utils';
import type { LocalizedText } from '@fame/shared';
import {
  ArrowLeft,
  Ban,
  ChevronDown,
  Code,
  Edit2,
  FileText,
  GitCompare,
  Merge as MergeIcon,
  Pencil,
  Plus,
  RefreshCw,
  RotateCcw,
  Save,
  Trash2,
  X,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

type BadgeVariant = NonNullable<BadgeProps['variant']>;

interface FieldRowProps {
  label: string;
  value: string;
  type?: 'string' | 'boolean';
  isOverridden?: boolean;
  originalValue?: string;
  isEditing?: boolean;
  onValueChange?: (value: string) => void;
  onRestore?: () => void;
  restoreDisabled?: boolean;
}

const CALCULATION_OVERRIDE_KEYS = new Set<string>([
  'number_of_participants',
  'calc_single_room',
  'calc_insurance',
  'activity_1',
  'activity_2',
  'activity_3',
  'activity_4',
  'activity_5',
  'p2_activity_1',
  'p2_activity_2',
  'p2_activity_3',
  'p2_activity_4',
  'p2_activity_5',
]);
const DEPOSIT_INVOICE_REQUIRED_PAYMENT_STATUSES = new Set(['paid', 'failed', 'expired', 'canceled']);

/**
 * Fields that are candidates for the submission diff panel.
 * Only fields listed here will appear in the diff. This also acts as the exclusion list
 * for generated/derived fields like _uuid, _name, logic_*, etc.
 */
const SUBMISSION_FIELD_LABELS: Record<string, string> = {
  // Person 1 – contact & billing
  email: 'E-mailadres',
  invoice_first_name: 'Voornaam',
  invoice_last_name: 'Achternaam',
  invoice_company: 'Bedrijf',
  invoice_vat: 'BTW-nummer',
  invoice_vat_number: 'BTW-nummer',
  invoice_vat_liable: 'BTW-plichtig',
  invoice_street: 'Straat',
  invoice_house_number: 'Huisnummer',
  invoice_zip: 'Postcode',
  invoice_city: 'Gemeente',
  invoice_country: 'Land',
  // Booking
  number_of_participants: 'Aantal deelnemers',
  calc_single_room: 'Single room supplement',
  calc_insurance: 'Verzekering',
  activity_1: 'Activiteit 1',
  activity_2: 'Activiteit 2',
  activity_3: 'Activiteit 3',
  activity_4: 'Activiteit 4',
  activity_5: 'Activiteit 5',
  logic_split_billing: 'Gesplitste facturatie',
  // Person 2 (split billing)
  p2_email: 'P2 Primair e-mailadres',
  p2_invoice_email: 'P2 Factuur e-mailadres',
  p2_invoice_first_name: 'P2 Voornaam',
  p2_invoice_last_name: 'P2 Achternaam',
  p2_invoice_company: 'P2 Bedrijf',
  p2_invoice_vat: 'P2 BTW-nummer',
  p2_invoice_vat_liable: 'P2 BTW-plichtig',
  p2_invoice_street: 'P2 Straat',
  p2_invoice_house_number: 'P2 Huisnummer',
  p2_invoice_zip: 'P2 Postcode',
  p2_invoice_city: 'P2 Gemeente',
  p2_invoice_country: 'P2 Land',
  p2_activity_1: 'P2 Activiteit 1',
  p2_activity_2: 'P2 Activiteit 2',
  p2_activity_3: 'P2 Activiteit 3',
  p2_activity_4: 'P2 Activiteit 4',
  p2_activity_5: 'P2 Activiteit 5',
};

type InvoiceAdjustmentScope = 'non_split' | 'person1' | 'person2';

type InvoiceAdjustment =
  | {
      type: 'set_line_amount';
      scope: InvoiceAdjustmentScope;
      targetArticleNumber: string;
      amount: string;
    }
  | {
      type: 'add_line';
      scope: InvoiceAdjustmentScope;
      articleNumber: string;
      description: string;
      amount: string;
    };

interface InvoiceAdjustmentDraft {
  type: 'set_line_amount' | 'add_line';
  scope: InvoiceAdjustmentScope;
  targetArticleNumber: string;
  articleNumber: string;
  description: string;
  amount: string;
}

function formatBooleanDisplay(value: string): string {
  if (!value) return '';
  const lower = value.toLowerCase();
  if (lower === '1' || lower === 'true' || lower === 'yes' || lower === 'ja') return 'Ja';
  if (lower === '0' || lower === 'false' || lower === 'no' || lower === 'nee') return 'Nee';
  return value;
}

function FieldRow({
  label,
  value,
  type,
  isOverridden,
  originalValue,
  isEditing,
  onValueChange,
  onRestore,
  restoreDisabled,
}: FieldRowProps) {
  const displayValue = type === 'boolean' ? formatBooleanDisplay(value) : value;

  return (
    <div className="space-y-1">
      <Label className="text-sm text-muted-foreground flex items-center gap-2">
        {label}
        {isOverridden && (
          <Badge variant="outline" className="text-xs">
            Aangepast
          </Badge>
        )}
        {onRestore && <RestoreFieldButton label={label} onRestore={onRestore} disabled={restoreDisabled} />}
      </Label>
      {isEditing && onValueChange ? (
        type === 'boolean' ? (
          <Select value={value || '_empty'} onValueChange={v => onValueChange(v === '_empty' ? '' : v)}>
            <SelectTrigger className="font-medium">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_empty">-</SelectItem>
              <SelectItem value="1">Ja</SelectItem>
              <SelectItem value="0">Nee</SelectItem>
            </SelectContent>
          </Select>
        ) : (
          <Input value={value} onChange={e => onValueChange(e.target.value)} className="font-medium" />
        )
      ) : (
        <p className={`font-medium ${isOverridden ? 'text-primary' : ''}`}>{displayValue}</p>
      )}
      {isOverridden && originalValue && !isEditing && (
        <p className="text-xs text-muted-foreground line-through">
          Origineel: {type === 'boolean' ? formatBooleanDisplay(originalValue) : originalValue}
        </p>
      )}
    </div>
  );
}

function RestoreFieldButton({
  label,
  onRestore,
  disabled,
}: {
  label: string;
  onRestore: () => void;
  disabled?: boolean;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="h-6 w-6"
      aria-label={`Origineel herstellen: ${label}`}
      title={`Origineel herstellen: ${label}`}
      disabled={disabled}
      onClick={onRestore}
    >
      <RotateCcw className="h-3.5 w-3.5" />
    </Button>
  );
}

export function RegistrationDetailPage() {
  const { groupId, id } = useParams<{ groupId: string; id: string }>();
  const navigate = useNavigate();
  const { data: registration, isLoading } = useRegistration(id!);
  const effectiveGroupId = registration?.group?.id ?? groupId ?? '';
  const { data: groupRegistrations = [] } = useGroupRegistrations(effectiveGroupId);
  const projectId = registration?.group?.projectId;
  const { data: project } = useProject(projectId || '');
  const { data: projectActivities = [] } = useActivities(projectId, false, Boolean(projectId));
  const { data: projectInsurances = [] } = useInsurances(projectId, false, Boolean(projectId));
  const updateRegistration = useUpdateRegistration();
  const createBalanceInvoice = useCreateBalanceInvoice();
  const retryDepositInvoice = useRetryDepositInvoice();
  const markInvoiceManuallyResolved = useMarkInvoiceManuallyResolved();
  const regenerateDepositInvoices = useRegenerateDepositInvoices();
  const mergeRegistration = useMergeRegistration();
  const cancelRegistration = useCancelRegistration();
  const changeParticipantCount = useChangeParticipantCount();
  const reactivateRegistration = useReactivateRegistration();

  const [isEditMode, setIsEditMode] = useState(false);
  const [editedOverrides, setEditedOverrides] = useState<Record<string, unknown>>({});
  const [restoreTarget, setRestoreTarget] = useState<{ fieldKey: string; label: string } | null>(null);
  const [editedSplitBilling, setEditedSplitBilling] = useState(false);
  const [editedInvoiceAdjustments, setEditedInvoiceAdjustments] = useState<InvoiceAdjustment[]>([]);
  const [editingAdjustmentIndex, setEditingAdjustmentIndex] = useState<number | null>(null);
  const [isInvoiceAdjustmentsOpen, setIsInvoiceAdjustmentsOpen] = useState(false);
  const [adjustmentDraft, setAdjustmentDraft] = useState<InvoiceAdjustmentDraft>({
    type: 'set_line_amount',
    scope: 'non_split',
    targetArticleNumber: '',
    articleNumber: '',
    description: '',
    amount: '',
  });
  const [expandedSubmissions, setExpandedSubmissions] = useState<Set<string>>(new Set());
  const [selectedDiffFields, setSelectedDiffFields] = useState<Set<string> | null>(null);
  const [selectedMergeTargetId, setSelectedMergeTargetId] = useState('');
  const [mergeReason, setMergeReason] = useState('');
  const [isMergeDialogOpen, setIsMergeDialogOpen] = useState(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isReactivateDialogOpen, setIsReactivateDialogOpen] = useState(false);
  const [manualResolutionInvoiceId, setManualResolutionInvoiceId] = useState<string | null>(null);
  const [participantCountTarget, setParticipantCountTarget] = useState<1 | 2 | null>(null);
  const [removedPerson, setRemovedPerson] = useState<'1' | '2'>('2');
  const [newPerson2FirstName, setNewPerson2FirstName] = useState('');
  const [newPerson2LastName, setNewPerson2LastName] = useState('');
  const [newPerson2Email, setNewPerson2Email] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const scopeLabels: Record<InvoiceAdjustmentScope, string> = {
    non_split: 'Gezamenlijk',
    person1: 'Persoon 1',
    person2: 'Persoon 2',
  };
  const amountPattern = /^[+-]?\d+([.,]\d{1,2})?$/;
  const savedRegistrationAdjustments = (registration?.invoiceAdjustments as InvoiceAdjustment[] | null) ?? [];
  const groupInvoiceAdjustments =
    (registration?.group?.invoiceAdjustments as Array<{
      type: 'add_line';
      articleNumber: string;
      description: LocalizedText | string;
      amount: string;
    }> | null) ?? [];
  const displayedAdjustments = isEditMode ? editedInvoiceAdjustments : savedRegistrationAdjustments;

  const sourceP2Email = useMemo(() => {
    if (!registration) {
      return '';
    }
    const p2Email = getEffectiveRegistrationField(
      registration.currentData,
      registration.manualOverrides,
      'p2_email',
      ''
    )
      .value.trim()
      .toLowerCase();
    const p2InvoiceEmail = getEffectiveRegistrationField(
      registration.currentData,
      registration.manualOverrides,
      'p2_invoice_email',
      ''
    )
      .value.trim()
      .toLowerCase();
    return p2Email || p2InvoiceEmail;
  }, [registration]);

  const mergeTargetCandidates = useMemo(() => {
    if (!registration) {
      return [];
    }

    const candidates = groupRegistrations.filter(item => item.id !== registration.id);
    return candidates.sort((a, b) => {
      const aIsSuggested = sourceP2Email && a.email.toLowerCase() === sourceP2Email ? 1 : 0;
      const bIsSuggested = sourceP2Email && b.email.toLowerCase() === sourceP2Email ? 1 : 0;
      if (aIsSuggested !== bIsSuggested) {
        return bIsSuggested - aIsSuggested;
      }
      return new Date(b.lastSubmittedAt).getTime() - new Date(a.lastSubmittedAt).getTime();
    });
  }, [groupRegistrations, registration, sourceP2Email]);

  const suggestedMergeTarget = useMemo(() => {
    if (!sourceP2Email) {
      return null;
    }
    return mergeTargetCandidates.find(candidate => candidate.email.toLowerCase() === sourceP2Email) || null;
  }, [mergeTargetCandidates, sourceP2Email]);

  const effectiveSelectedMergeTargetId = selectedMergeTargetId || suggestedMergeTarget?.id || '';
  const selectedMergeTarget = useMemo(
    () => mergeTargetCandidates.find(candidate => candidate.id === effectiveSelectedMergeTargetId) || null,
    [effectiveSelectedMergeTargetId, mergeTargetCandidates]
  );

  const mergePreview = useMemo(() => {
    if (!registration || !selectedMergeTarget) {
      return null;
    }

    const targetCurrent = selectedMergeTarget.currentData || {};
    const sourceCurrent = registration.currentData || {};
    const targetOverrides = selectedMergeTarget.manualOverrides || {};
    const sourceOverrides = registration.manualOverrides || {};

    const currentAdded = Object.entries(sourceCurrent).filter(([key, value]) => {
      const targetValue = targetCurrent[key];
      return (targetValue === null || targetValue === undefined) && value !== null && value !== undefined;
    });
    const currentSkipped = Object.entries(sourceCurrent).filter(([key]) => {
      const targetValue = targetCurrent[key];
      return targetValue !== null && targetValue !== undefined;
    });

    const overrideAdded = Object.entries(sourceOverrides).filter(([key, value]) => {
      const targetValue = targetOverrides[key];
      return (targetValue === null || targetValue === undefined) && value !== null && value !== undefined;
    });
    const overrideSkipped = Object.entries(sourceOverrides).filter(([key]) => {
      const targetValue = targetOverrides[key];
      return targetValue !== null && targetValue !== undefined;
    });

    return {
      movedSubmissions: registration.submissions.length,
      movedPayments: registration.payments.length,
      movedInvoices: registration.invoices.length,
      currentAddedKeys: currentAdded.map(([key]) => key),
      currentSkippedCount: currentSkipped.length,
      overrideAddedKeys: overrideAdded.map(([key]) => key),
      overrideSkippedCount: overrideSkipped.length,
    };
  }, [registration, selectedMergeTarget]);

  const invoicePreviewPayload = useMemo(() => {
    if (!registration) {
      return {};
    }

    if (!isEditMode) {
      return {};
    }

    const manualOverrides: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(editedOverrides)) {
      if (!CALCULATION_OVERRIDE_KEYS.has(key)) {
        continue;
      }
      manualOverrides[key] = value;
    }

    return {
      manualOverrides,
      isSplitBilling: editedSplitBilling,
      invoiceAdjustments: editedInvoiceAdjustments,
    };
  }, [editedInvoiceAdjustments, editedOverrides, editedSplitBilling, isEditMode, registration]);

  const { data: invoicePreview, isLoading: isInvoicePreviewLoading } = useRegistrationInvoicePreview(
    id || '',
    invoicePreviewPayload
  );

  const availableTargetArticles = useMemo(() => {
    if (!invoicePreview) {
      return [] as Array<{ articleNumber: string; label: string }>;
    }

    const effectiveScope = editedSplitBilling ? adjustmentDraft.scope : 'non_split';
    const scopedLines =
      effectiveScope === 'non_split'
        ? (invoicePreview.nonSplit?.lines ?? [])
        : effectiveScope === 'person1'
          ? (invoicePreview.person1?.lines ?? [])
          : (invoicePreview.person2?.lines ?? []);

    const uniqueByArticle = new Map<string, string>();
    for (const line of scopedLines) {
      const articleNumber = line.articleNumber?.trim() || line.itemCode?.trim();
      if (!articleNumber) {
        continue;
      }
      if (!uniqueByArticle.has(articleNumber)) {
        uniqueByArticle.set(articleNumber, line.label);
      }
    }

    // Fallback options from project config in case a base line article is missing in preview metadata.
    if (project?.balanceArticleNumber?.trim() && !uniqueByArticle.has(project.balanceArticleNumber.trim())) {
      uniqueByArticle.set(project.balanceArticleNumber.trim(), 'Pakketprijs');
    }
    if (project?.depositArticleNumber?.trim() && !uniqueByArticle.has(project.depositArticleNumber.trim())) {
      uniqueByArticle.set(project.depositArticleNumber.trim(), 'Voorschot');
    }
    if (project?.singleRoomArticleNumber?.trim() && !uniqueByArticle.has(project.singleRoomArticleNumber.trim())) {
      uniqueByArticle.set(project.singleRoomArticleNumber.trim(), 'Single-room supplement');
    }
    if (project?.activityArticleNumber?.trim() && !uniqueByArticle.has(project.activityArticleNumber.trim())) {
      uniqueByArticle.set(project.activityArticleNumber.trim(), 'Activiteiten');
    }

    return [...uniqueByArticle.entries()].map(([articleNumber, label]) => ({ articleNumber, label }));
  }, [adjustmentDraft.scope, editedSplitBilling, invoicePreview, project]);

  /**
   * Compute a "last-wins" snapshot from all submissions for fields in SUBMISSION_FIELD_LABELS,
   * then diff against the effective current values. Only shown when >1 submissions exist.
   */
  const submissionDiff = useMemo(() => {
    if (!registration || registration.submissions.length <= 1) return [];

    // Build last-wins snapshot: later submissions can overwrite earlier ones
    const latestSnapshot: Record<string, string> = {};
    const sortedSubmissions = [...registration.submissions].sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
    for (const submission of sortedSubmissions) {
      const data = submission.normalizedData as Record<string, unknown> | null;
      if (!data) continue;
      for (const key of Object.keys(SUBMISSION_FIELD_LABELS)) {
        const val = data[key];
        if (val != null && val !== '') {
          latestSnapshot[key] = String(val).trim();
        }
      }
    }

    // Compare snapshot to effective current values (manualOverrides take precedence)
    const diffs: Array<{ key: string; label: string; currentValue: string; latestValue: string }> = [];
    for (const [key, label] of Object.entries(SUBMISSION_FIELD_LABELS)) {
      const latestValue = latestSnapshot[key];
      if (latestValue == null) continue;

      const { value: currentValue } = getEffectiveRegistrationField(
        registration.currentData as Record<string, unknown>,
        registration.manualOverrides as Record<string, unknown>,
        key,
        ''
      );

      if (latestValue.toLowerCase() !== currentValue.toLowerCase()) {
        diffs.push({ key, label, currentValue, latestValue });
      }
    }
    return diffs;
  }, [registration]);

  const defaultSelectedDiffFields = useMemo(() => new Set(submissionDiff.map(diff => diff.key)), [submissionDiff]);
  const activeSelectedDiffFields = selectedDiffFields ?? defaultSelectedDiffFields;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!registration) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Registratie niet gevonden</p>
        <Button asChild variant="link">
          <Link to={`/groepen/${groupId}`}>Terug naar groep</Link>
        </Button>
      </div>
    );
  }

  const handleStartEdit = () => {
    if (hasBalanceInvoice || registration.cancelledAt || updateRegistration.isPending) {
      return;
    }
    setRestoreTarget(null);
    setEditedOverrides(registration.manualOverrides || {});
    setEditedSplitBilling(registration.isSplitBilling);
    setEditedInvoiceAdjustments((registration.invoiceAdjustments as InvoiceAdjustment[] | null) ?? []);
    setEditingAdjustmentIndex(null);
    setAdjustmentDraft({
      type: 'set_line_amount',
      scope: registration.isSplitBilling ? 'person1' : 'non_split',
      targetArticleNumber: '',
      articleNumber: '',
      description: '',
      amount: '',
    });
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    setRestoreTarget(null);
    setEditedOverrides({});
    setEditedInvoiceAdjustments([]);
    setEditingAdjustmentIndex(null);
    setIsEditMode(false);
  };

  const handleSaveEdit = async () => {
    setRestoreTarget(null);
    await updateRegistration.mutateAsync({
      id: registration.id,
      manualOverrides: editedOverrides,
      isSplitBilling: editedSplitBilling,
      invoiceAdjustments: editedInvoiceAdjustments,
    });
    setIsEditMode(false);
  };

  const handleConfirmMerge = async () => {
    if (!registration || !effectiveSelectedMergeTargetId) {
      return;
    }

    await mergeRegistration.mutateAsync({
      targetRegistrationId: effectiveSelectedMergeTargetId,
      sourceRegistrationId: registration.id,
      reason: mergeReason.trim() || undefined,
    });

    setIsMergeDialogOpen(false);
    navigate(`/groepen/${effectiveGroupId}/registraties/${effectiveSelectedMergeTargetId}`);
  };

  const handleConfirmCancel = async () => {
    if (!registration || !cancelReason.trim()) {
      return;
    }
    await cancelRegistration.mutateAsync({
      id: registration.id,
      reason: cancelReason.trim(),
    });
    setIsCancelDialogOpen(false);
    setCancelReason('');
  };

  const handleConfirmReactivate = async () => {
    if (!registration) {
      return;
    }
    await reactivateRegistration.mutateAsync({ id: registration.id });
    setIsReactivateDialogOpen(false);
  };

  const handleConfirmManualResolution = async () => {
    if (!registration || !manualResolutionInvoiceId) {
      return;
    }
    await markInvoiceManuallyResolved.mutateAsync({
      registrationId: registration.id,
      invoiceId: manualResolutionInvoiceId,
    });
    setManualResolutionInvoiceId(null);
  };

  const handleChangeParticipantCount = async () => {
    if (!registration || !participantCountTarget) return;
    await changeParticipantCount.mutateAsync({
      id: registration.id,
      participantCount: participantCountTarget,
      ...(participantCountTarget === 1
        ? { removedPerson: Number(removedPerson) as 1 | 2 }
        : {
            person2: {
              firstName: newPerson2FirstName.trim(),
              lastName: newPerson2LastName.trim(),
              email: newPerson2Email.trim(),
            },
          }),
    });
    setParticipantCountTarget(null);
    setNewPerson2FirstName('');
    setNewPerson2LastName('');
    setNewPerson2Email('');
  };

  const handleApplyDiff = async () => {
    const newOverrides = { ...((registration.manualOverrides as Record<string, unknown>) ?? {}) };
    for (const diff of submissionDiff) {
      if (activeSelectedDiffFields.has(diff.key)) {
        newOverrides[diff.key] = diff.latestValue;
      }
    }
    await updateRegistration.mutateAsync({
      id: registration.id,
      manualOverrides: newOverrides,
    });
  };

  const updateField = (fieldKey: string, value: string) => {
    setEditedOverrides(prev => ({
      ...prev,
      [fieldKey]: value,
    }));
  };

  const resetAdjustmentDraft = (scope: InvoiceAdjustmentScope) => {
    setAdjustmentDraft({
      type: 'set_line_amount',
      scope,
      targetArticleNumber: '',
      articleNumber: '',
      description: '',
      amount: '',
    });
    setEditingAdjustmentIndex(null);
  };

  const handleSplitBillingToggle = (nextValue: boolean) => {
    setEditedSplitBilling(nextValue);
    const remappedScope: InvoiceAdjustmentScope = nextValue ? 'person1' : 'non_split';
    setEditedInvoiceAdjustments(prev =>
      prev.map(adjustment => ({
        ...adjustment,
        scope: nextValue ? (adjustment.scope === 'non_split' ? 'person1' : adjustment.scope) : 'non_split',
      }))
    );
    setAdjustmentDraft(prev => ({
      ...prev,
      scope: prev.scope === 'non_split' && nextValue ? 'person1' : !nextValue ? 'non_split' : prev.scope,
    }));
    if (!nextValue) {
      resetAdjustmentDraft(remappedScope);
    }
  };

  const handleEditAdjustment = (adjustment: InvoiceAdjustment, index: number) => {
    setEditingAdjustmentIndex(index);
    if (adjustment.type === 'set_line_amount') {
      setAdjustmentDraft({
        type: 'set_line_amount',
        scope: adjustment.scope,
        targetArticleNumber: adjustment.targetArticleNumber,
        articleNumber: '',
        description: '',
        amount: adjustment.amount,
      });
      return;
    }

    setAdjustmentDraft({
      type: 'add_line',
      scope: adjustment.scope,
      targetArticleNumber: '',
      articleNumber: adjustment.articleNumber,
      description: adjustment.description,
      amount: adjustment.amount,
    });
  };

  const handleRemoveAdjustment = (index: number) => {
    setEditedInvoiceAdjustments(prev => prev.filter((_, i) => i !== index));
    if (editingAdjustmentIndex === index) {
      resetAdjustmentDraft(editedSplitBilling ? 'person1' : 'non_split');
    }
  };

  const handleSubmitAdjustment = () => {
    const scope = editedSplitBilling ? adjustmentDraft.scope : 'non_split';
    const normalizedAmount = adjustmentDraft.amount.trim();
    if (!amountPattern.test(normalizedAmount)) {
      return;
    }

    let nextAdjustment: InvoiceAdjustment;
    if (adjustmentDraft.type === 'set_line_amount') {
      const targetArticleNumber = adjustmentDraft.targetArticleNumber.trim();
      if (!targetArticleNumber) {
        return;
      }
      nextAdjustment = {
        type: 'set_line_amount',
        scope,
        targetArticleNumber,
        amount: normalizedAmount,
      };
    } else {
      const articleNumber = adjustmentDraft.articleNumber.trim();
      const description = adjustmentDraft.description.trim();
      if (!articleNumber || !description) {
        return;
      }
      nextAdjustment = {
        type: 'add_line',
        scope,
        articleNumber,
        description,
        amount: normalizedAmount,
      };
    }

    setEditedInvoiceAdjustments(prev => {
      if (editingAdjustmentIndex == null) {
        return [...prev, nextAdjustment];
      }
      return prev.map((item, index) => (index === editingAdjustmentIndex ? nextAdjustment : item));
    });
    resetAdjustmentDraft(editedSplitBilling ? 'person1' : 'non_split');
  };

  const updateActivityField = (fieldKey: string, value: string) => {
    const nameKey = `${fieldKey}_name`;
    const uuidKey = `${fieldKey}_uuid`;

    setEditedOverrides(prev => ({
      ...prev,
      [fieldKey]: value,
      [nameKey]: '',
      [uuidKey]: '',
    }));
  };

  const getField = (fieldKey: string) => {
    if (isEditMode) {
      return getEffectiveRegistrationField(registration.currentData, editedOverrides, fieldKey, '').value;
    }
    return getEffectiveRegistrationField(registration.currentData, registration.manualOverrides, fieldKey);
  };

  // Personal info fields
  const firstName = getField('invoice_first_name');
  const lastName = getField('invoice_last_name');
  const company = getField('invoice_company');
  const vatNumber = getField('invoice_vat_number');

  // Billing address fields
  const street = getField('invoice_street');
  const houseNumber = getField('invoice_house_number');
  const zip = getField('invoice_zip');
  const city = getField('invoice_city');
  const country = getField('invoice_country');
  const vatLiable = getField('invoice_vat_liable');

  // Helper to get value from field (handles both string and object types)
  const getFieldValue = (field: string | ReturnType<typeof getEffectiveRegistrationField>) => {
    return typeof field === 'string' ? field : field.value;
  };

  // Helper to check if field is overridden
  const isFieldOverridden = (field: string | ReturnType<typeof getEffectiveRegistrationField>) => {
    return typeof field === 'string' ? false : field.isOverridden;
  };

  // Helper to get original value
  const getOriginalValue = (field: string | ReturnType<typeof getEffectiveRegistrationField>) => {
    return typeof field === 'string' ? '' : field.originalValue;
  };

  // P2 (split billing) fields
  const p2FirstName = getField('p2_invoice_first_name');
  const p2LastName = getField('p2_invoice_last_name');
  const p2PrimaryEmail = getField('p2_email');
  const p2Email = getField('p2_invoice_email');
  const p2Company = getField('p2_invoice_company');
  const p2VatNumber = getField('p2_invoice_vat_number');
  const p2VatLiable = getField('p2_invoice_vat_liable');
  const p2Street = getField('p2_invoice_street');
  const p2HouseNumber = getField('p2_invoice_house_number');
  const p2Zip = getField('p2_invoice_zip');
  const p2City = getField('p2_invoice_city');
  const p2Country = getField('p2_invoice_country');

  // Activity/calculation fields
  const numberOfParticipants = getField('number_of_participants');
  const singleRoom = getField('calc_single_room');
  const insurance = getField('calc_insurance');

  const parsedParticipantCount = Number.parseInt(getFieldValue(numberOfParticipants), 10);
  const fallbackParticipantCount = 1;
  const displayParticipantCount =
    Number.isFinite(parsedParticipantCount) && parsedParticipantCount > 0
      ? parsedParticipantCount
      : fallbackParticipantCount;

  const invoiceStatusColors: Record<string, BadgeVariant> = {
    DRAFT: 'secondary',
    QUEUED: 'default',
    SYNCED: 'success',
    FAILED: 'destructive',
    MATCHED: 'success',
    MANUALLY_RESOLVED: 'success',
  };
  const invoiceStatusLabels: Record<string, string> = {
    DRAFT: 'Concept',
    QUEUED: 'In wachtrij',
    SYNCED: 'Aangemaakt',
    FAILED: 'Mislukt',
    MATCHED: 'Gekoppeld',
    MANUALLY_RESOLVED: 'Handmatig',
  };
  const invoiceTypeLabels: Record<string, string> = {
    DEPOSIT: 'Voorschot',
    BALANCE: 'Saldo',
  };
  const paymentStatusColors: Record<string, BadgeVariant> = {
    created: 'secondary',
    pending: 'default',
    open: 'default',
    paid: 'success',
    failed: 'destructive',
    expired: 'destructive',
    canceled: 'secondary',
  };
  const paymentStatusLabels: Record<string, string> = {
    created: 'Aangemaakt',
    pending: 'In behandeling',
    open: 'Open',
    paid: 'Betaald',
    failed: 'Mislukt',
    expired: 'Verlopen',
    canceled: 'Geannuleerd',
  };
  const paymentInvoiceMap = new Map(
    registration.invoices
      .filter(invoice => invoice.type === 'DEPOSIT' && invoice.paymentId)
      .map(invoice => [invoice.paymentId as string, invoice])
  );
  const hasBalanceInvoice = registration.invoices.some(inv => inv.type === 'BALANCE');
  const isRegistrationClosed = hasBalanceInvoice;
  const canRestoreField =
    isEditMode && !isRegistrationClosed && !registration.cancelledAt && !updateRegistration.isPending;
  const restoreFieldProps = (fieldKey: string, label = SUBMISSION_FIELD_LABELS[fieldKey] || fieldKey) => ({
    onRestore:
      isEditMode && hasRegistrationFieldOverride(editedOverrides, fieldKey)
        ? () => {
            if (canRestoreField) setRestoreTarget({ fieldKey, label });
          }
        : undefined,
    restoreDisabled: !canRestoreField,
  });
  const restoreOriginalValue = restoreTarget
    ? (() => {
        const value = getEffectiveRegistrationField(
          registration.currentData,
          undefined,
          restoreTarget.fieldKey,
          ''
        ).value;
        if (!value.trim()) return '(leeg)';
        if (restoreTarget.fieldKey === 'calc_single_room' || restoreTarget.fieldKey.endsWith('invoice_vat_liable')) {
          return formatBooleanDisplay(value);
        }
        if (restoreTarget.fieldKey === 'calc_insurance' || /^(p2_)?activity_[1-5]$/.test(restoreTarget.fieldKey)) {
          const name = getEffectiveRegistrationField(
            registration.currentData,
            undefined,
            `${restoreTarget.fieldKey}_name`,
            ''
          ).value.trim();
          return name && name !== value ? `${name} (${value})` : value;
        }
        return value;
      })()
    : '';
  const handleConfirmRestore = () => {
    if (!restoreTarget || !canRestoreField) return;
    setEditedOverrides(previous => restoreRegistrationFieldOverride(previous, restoreTarget.fieldKey));
    setRestoreTarget(null);
  };
  // Two participants do not imply two invoices when split billing is disabled.
  const showSecondInvoice =
    displayParticipantCount > 1 && (isEditMode ? editedSplitBilling : registration.isSplitBilling);

  // Show "Regenerate deposit invoices" when there are paid payments without a
  // final deposit invoice (i.e. the invoice is FAILED, QUEUED, or missing).
  const canRegenerateDepositInvoices = registration.payments.some(
    p =>
      DEPOSIT_INVOICE_REQUIRED_PAYMENT_STATUSES.has(p.status) &&
      !registration.invoices.some(
        inv =>
          inv.type === 'DEPOSIT' &&
          inv.paymentId === p.id &&
          (inv.exactStatus === 'SYNCED' || inv.exactStatus === 'MATCHED' || inv.exactStatus === 'MANUALLY_RESOLVED')
      )
  );

  const activityOptions = projectActivities.map(activity => ({
    code: String(activity.code),
    label: `${activity.internalName.trim()} (${activity.code})`,
  }));
  const activityNameByCode = new Map(
    projectActivities.map(activity => [String(activity.code), activity.internalName.trim()])
  );

  const insuranceOptions = projectInsurances.map(ins => ({
    code: String(ins.code),
    label: `${ins.internalName.trim()} (${ins.code})`,
  }));
  const insuranceNameByCode = new Map(projectInsurances.map(ins => [String(ins.code), ins.internalName.trim()]));

  const getActivitiesForPerson = (prefix: '' | 'p2_') => {
    const activities: Array<{
      key: string;
      label: string;
      value: string;
      activityValue: string;
      nameValue: string;
      isOverridden: boolean;
      originalValue: string;
    }> = [];

    for (let i = 1; i <= 5; i++) {
      const key = `${prefix}activity_${i}`;
      const nameKey = `${key}_name`;
      const codeKey = key;
      const field = getField(key);
      const nameField = getField(nameKey);
      const codeField = getField(codeKey);
      const rawValue = isEditMode ? (editedOverrides[key] ?? getFieldValue(field)) : getFieldValue(field);
      const rawNameValue = isEditMode
        ? (editedOverrides[nameKey] ?? getFieldValue(nameField))
        : getFieldValue(nameField);
      const rawCodeValue = isEditMode
        ? (editedOverrides[codeKey] ?? getFieldValue(codeField))
        : getFieldValue(codeField);
      const value = rawValue == null ? '' : String(rawValue).trim();
      const nameValue = rawNameValue == null ? '' : String(rawNameValue).trim();
      const codeValue = rawCodeValue == null ? '' : String(rawCodeValue).trim();
      const normalized = value.toLowerCase();

      if (!value || normalized === '-' || normalized === '—' || normalized === 'n/a' || normalized === 'na') {
        continue;
      }

      const resolvedName = nameValue || activityNameByCode.get(value) || '';
      const normalizedName = resolvedName.toLowerCase();
      const hasDisplayName =
        !!resolvedName &&
        normalizedName !== '-' &&
        normalizedName !== '—' &&
        normalizedName !== 'n/a' &&
        normalizedName !== 'na' &&
        normalizedName !== normalized;

      const displaySuffix = codeValue || value;
      const displayValue = hasDisplayName ? `${resolvedName} (${displaySuffix})` : value;

      activities.push({
        key,
        label: `Activiteit ${i}`,
        value: displayValue,
        activityValue: value,
        nameValue,
        isOverridden: !isEditMode && isFieldOverridden(field),
        originalValue: getOriginalValue(field),
      });
    }

    if (!isEditMode) {
      return activities;
    }

    const highestFilledIndex = activities.reduce((max, activity) => {
      const index = Number.parseInt(activity.key.split('_').pop() || '0', 10);
      return index > max ? index : max;
    }, 0);

    const highestRestorableIndex = Array.from({ length: 5 }, (_, index) => index + 1).reduce(
      (highest, index) =>
        hasRegistrationFieldOverride(editedOverrides, `${prefix}activity_${index}`) ? index : highest,
      0
    );
    const slotCount = Math.min(5, Math.max(1, highestFilledIndex + 1, highestRestorableIndex));
    const byKey = new Map(activities.map(activity => [activity.key, activity]));
    const result = [];

    for (let i = 1; i <= slotCount; i++) {
      const key = `${prefix}activity_${i}`;
      const existing = byKey.get(key);
      if (existing) {
        result.push(existing);
        continue;
      }

      result.push({
        key,
        label: `Activiteit ${i}`,
        value: '',
        activityValue: '',
        nameValue: '',
        isOverridden: false,
        originalValue: '',
      });
    }

    return result;
  };

  const person1Activities = getActivitiesForPerson('');
  const person2Activities = getActivitiesForPerson('p2_');
  const formatSignedAmount = (amount: string) => {
    const numeric = Number.parseFloat(amount || '0');
    const prefix = numeric < 0 ? '-' : '';
    return `${prefix}${formatCurrency(Math.abs(numeric).toFixed(2))}`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to={`/groepen/${groupId}`}>
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">{registration.email}</h1>
          <p className="text-muted-foreground">{getLocalizedText(registration.group.name)} • Registratie details</p>
        </div>
        {!isEditMode ? (
          <div className="flex items-center gap-2">
            {registration.cancelledAt && <Badge variant="destructive">Geannuleerd</Badge>}
            {registration.isSplitBilling && <Badge variant="secondary">Split Billing</Badge>}
            {isRegistrationClosed && <Badge variant="warning">Gesloten</Badge>}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <span>Acties</span>
                  <ChevronDown className="ml-2 h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {registration.cancelledAt ? (
                  <DropdownMenuItem onClick={() => setIsReactivateDialogOpen(true)} disabled={isRegistrationClosed}>
                    <RotateCcw className="h-4 w-4" />
                    Heractiveren
                  </DropdownMenuItem>
                ) : (
                  <>
                    <DropdownMenuItem onClick={handleStartEdit} disabled={isRegistrationClosed}>
                      <Pencil className="h-4 w-4" />
                      Bewerken
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => setIsMergeDialogOpen(true)}
                      disabled={isRegistrationClosed || mergeTargetCandidates.length === 0}
                    >
                      <MergeIcon className="h-4 w-4" />
                      Samenvoegen
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-destructive"
                      onClick={() => setIsCancelDialogOpen(true)}
                      disabled={isRegistrationClosed}
                    >
                      <Ban className="h-4 w-4" />
                      Annuleren
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Label htmlFor="split-billing-toggle" className="text-sm">
                Split Billing
              </Label>
              <Switch
                id="split-billing-toggle"
                checked={editedSplitBilling}
                onCheckedChange={handleSplitBillingToggle}
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleCancelEdit}>
                <X className="mr-2 h-4 w-4" />
                Annuleren
              </Button>
              <Button size="sm" onClick={handleSaveEdit} disabled={updateRegistration.isPending}>
                <Save className="mr-2 h-4 w-4" />
                Opslaan
              </Button>
            </div>
          </div>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Registratie Informatie</CardTitle>
          <CardDescription>Overzicht van indieningen</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Eerste indiening</p>
              <p className="font-medium">{formatDateTime(registration.firstSubmittedAt)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Laatste indiening</p>
              <p className="font-medium">{formatDateTime(registration.lastSubmittedAt)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Aantal indieningen</p>
              <p className="font-medium">{registration.submissions.length}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <AlertDialog
        open={restoreTarget !== null}
        onOpenChange={open => {
          if (!open) setRestoreTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Origineel herstellen?</AlertDialogTitle>
            <AlertDialogDescription>
              De handmatige aanpassing voor {restoreTarget?.label} wordt uit je concept verwijderd. Pas na Opslaan wordt
              het origineel definitief hersteld. Annuleren van de bewerking behoudt de opgeslagen aanpassing.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">Oorspronkelijk ingediende waarde</p>
            <p className="font-medium whitespace-pre-wrap">{restoreOriginalValue}</p>
          </div>
          <AlertDialogFooter>
            <Button variant="outline" onClick={() => setRestoreTarget(null)}>
              Annuleren
            </Button>
            <Button disabled={!canRestoreField} onClick={handleConfirmRestore}>
              Origineel herstellen
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={isMergeDialogOpen} onOpenChange={setIsMergeDialogOpen}>
        <AlertDialogContent className="max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Samenvoegen in andere registratie</AlertDialogTitle>
            <AlertDialogDescription>
              Deze registratie is de bron. Kies hieronder de doelregistratie die verrijkt moet worden. Bestaande waarden
              in het doel blijven behouden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Doelregistratie</Label>
              <Select
                value={effectiveSelectedMergeTargetId || '_empty'}
                onValueChange={value => setSelectedMergeTargetId(value === '_empty' ? '' : value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecteer doelregistratie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_empty">-</SelectItem>
                  {mergeTargetCandidates.map(candidate => {
                    const isSuggested = suggestedMergeTarget?.id === candidate.id;
                    const company = getEffectiveRegistrationField(
                      candidate.currentData,
                      candidate.manualOverrides,
                      'invoice_company',
                      ''
                    ).value.trim();
                    const companyPart = company ? ` • ${company}` : '';
                    const submittedPart = ` • ${formatDateTime(candidate.lastSubmittedAt)}`;
                    const suggestionPart = isSuggested ? ' • Suggestie (P2 e-mail)' : '';
                    const label = `${candidate.email}${companyPart}${submittedPart}${suggestionPart}`;
                    return (
                      <SelectItem key={candidate.id} value={candidate.id}>
                        {label}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              {suggestedMergeTarget && (
                <p className="text-xs text-muted-foreground">
                  Suggestie op basis van P2 e-mail: <span className="font-medium">{suggestedMergeTarget.email}</span>
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Reden (optioneel)</Label>
              <Textarea
                value={mergeReason}
                onChange={e => setMergeReason(e.target.value)}
                placeholder="Bijv. dubbele registratie door omgewisselde e-mails van persoon 1 en 2"
              />
            </div>

            {mergePreview ? (
              <div className="rounded-lg border p-4 space-y-3 text-sm">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <div>
                    <p className="text-muted-foreground">Te verplaatsen indieningen</p>
                    <p className="font-medium">{mergePreview.movedSubmissions}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Te verplaatsen betalingen</p>
                    <p className="font-medium">{mergePreview.movedPayments}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Te verplaatsen facturen</p>
                    <p className="font-medium">{mergePreview.movedInvoices}</p>
                  </div>
                </div>
                <div>
                  <p className="text-muted-foreground">Nieuwe velden voor doel (current_data)</p>
                  <p className="font-medium">
                    {mergePreview.currentAddedKeys.length > 0
                      ? mergePreview.currentAddedKeys.slice(0, 8).join(', ')
                      : 'Geen'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Overgeslagen (bestond al in doel): {mergePreview.currentSkippedCount}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Nieuwe velden voor doel (manual_overrides)</p>
                  <p className="font-medium">
                    {mergePreview.overrideAddedKeys.length > 0
                      ? mergePreview.overrideAddedKeys.slice(0, 8).join(', ')
                      : 'Geen'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Overgeslagen (bestond al in doel): {mergePreview.overrideSkippedCount}
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                Selecteer eerst een doelregistratie om de preview te zien.
              </p>
            )}
          </div>
          <AlertDialogFooter>
            <Button variant="outline" onClick={() => setIsMergeDialogOpen(false)}>
              Sluiten
            </Button>
            <Button
              disabled={!effectiveSelectedMergeTargetId || mergeRegistration.isPending}
              onClick={() => void handleConfirmMerge()}
              variant="destructive"
            >
              {mergeRegistration.isPending ? 'Bezig...' : 'Samenvoegen uitvoeren'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={manualResolutionInvoiceId !== null}
        onOpenChange={open => {
          if (!open && !markInvoiceManuallyResolved.isPending) {
            setManualResolutionInvoiceId(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Factuur handmatig markeren?</AlertDialogTitle>
            <AlertDialogDescription>
              Dit stopt verdere pogingen om deze voorschotfactuur naar Exact te synchroniseren. Deze actie kan niet
              ongedaan worden gemaakt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button
              variant="outline"
              disabled={markInvoiceManuallyResolved.isPending}
              onClick={() => setManualResolutionInvoiceId(null)}
            >
              Annuleren
            </Button>
            <Button
              variant="destructive"
              disabled={markInvoiceManuallyResolved.isPending}
              onClick={() => void handleConfirmManualResolution()}
            >
              {markInvoiceManuallyResolved.isPending ? 'Bezig...' : 'Handmatig markeren'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={isCancelDialogOpen} onOpenChange={setIsCancelDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Registratie annuleren</AlertDialogTitle>
            <AlertDialogDescription>
              Deze registratie blijft zichtbaar in de lijst als geannuleerd. Facturen en betalingen blijven historisch
              bewaard.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <Label>Reden</Label>
            <Textarea
              value={cancelReason}
              onChange={e => setCancelReason(e.target.value)}
              placeholder="Waarom wordt deze registratie geannuleerd?"
            />
          </div>
          <AlertDialogFooter>
            <Button variant="outline" onClick={() => setIsCancelDialogOpen(false)}>
              Sluiten
            </Button>
            <Button
              variant="destructive"
              disabled={!cancelReason.trim() || cancelRegistration.isPending}
              onClick={() => void handleConfirmCancel()}
            >
              {cancelRegistration.isPending ? 'Bezig...' : 'Annuleren bevestigen'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={isReactivateDialogOpen} onOpenChange={setIsReactivateDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Registratie heractiveren</AlertDialogTitle>
            <AlertDialogDescription>
              Deze registratie wordt opnieuw actief gemaakt en verliest de status geannuleerd.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" onClick={() => setIsReactivateDialogOpen(false)}>
              Sluiten
            </Button>
            <Button disabled={reactivateRegistration.isPending} onClick={() => void handleConfirmReactivate()}>
              {reactivateRegistration.isPending ? 'Bezig...' : 'Heractiveren'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={participantCountTarget !== null}
        onOpenChange={open => {
          if (!open) setParticipantCountTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {participantCountTarget === 2 ? 'Deelnemer toevoegen' : 'Aantal deelnemers wijzigen'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {participantCountTarget === 2
                ? 'Voornaam en achternaam van deelnemer 2 zijn verplicht. Een e-mailadres is optioneel.'
                : 'Kies expliciet welke deelnemer uit deze registratie verwijderd wordt.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {participantCountTarget === 2 ? (
            <div className="grid gap-3">
              <div className="space-y-1">
                <Label htmlFor="new-person2-first-name">Voornaam *</Label>
                <Input
                  id="new-person2-first-name"
                  value={newPerson2FirstName}
                  onChange={event => setNewPerson2FirstName(event.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="new-person2-last-name">Achternaam *</Label>
                <Input
                  id="new-person2-last-name"
                  value={newPerson2LastName}
                  onChange={event => setNewPerson2LastName(event.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="new-person2-email">E-mailadres (optioneel)</Label>
                <Input
                  id="new-person2-email"
                  type="email"
                  value={newPerson2Email}
                  onChange={event => setNewPerson2Email(event.target.value)}
                />
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <Label>Te verwijderen deelnemer</Label>
              <Select value={removedPerson} onValueChange={value => setRemovedPerson(value as '1' | '2')}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Persoon 1 verwijderen</SelectItem>
                  <SelectItem value="2">Persoon 2 verwijderen</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <AlertDialogFooter>
            <Button variant="outline" onClick={() => setParticipantCountTarget(null)}>
              Annuleren
            </Button>
            <Button
              disabled={
                changeParticipantCount.isPending ||
                (participantCountTarget === 2 && (!newPerson2FirstName.trim() || !newPerson2LastName.trim()))
              }
              onClick={() => void handleChangeParticipantCount()}
            >
              {changeParticipantCount.isPending ? 'Bezig...' : 'Bevestigen'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Persoonlijke Gegevens
            {isEditMode && <Edit2 className="h-4 w-4 text-muted-foreground" />}
          </CardTitle>
          <CardDescription>Persoon 1 en Persoon 2</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4 rounded-lg border p-4">
            <h3 className="font-semibold">Persoon 1</h3>
            <FieldRow
              label="Voornaam"
              value={
                isEditMode
                  ? String(editedOverrides.invoice_first_name || getFieldValue(firstName))
                  : getFieldValue(firstName)
              }
              isOverridden={!isEditMode && isFieldOverridden(firstName)}
              originalValue={getOriginalValue(firstName)}
              isEditing={isEditMode}
              onValueChange={v => updateField('invoice_first_name', v)}
              {...restoreFieldProps('invoice_first_name', 'Voornaam')}
            />
            <FieldRow
              label="Achternaam"
              value={
                isEditMode
                  ? String(editedOverrides.invoice_last_name || getFieldValue(lastName))
                  : getFieldValue(lastName)
              }
              isOverridden={!isEditMode && isFieldOverridden(lastName)}
              originalValue={getOriginalValue(lastName)}
              isEditing={isEditMode}
              onValueChange={v => updateField('invoice_last_name', v)}
              {...restoreFieldProps('invoice_last_name', 'Achternaam')}
            />
            <FieldRow label="E-mailadres" value={registration.email} isOverridden={false} isEditing={false} />
          </div>

          <div className="space-y-4 rounded-lg border p-4">
            <h3 className="font-semibold">Persoon 2</h3>
            <FieldRow
              label="Voornaam"
              value={
                isEditMode
                  ? String(editedOverrides.p2_invoice_first_name || getFieldValue(p2FirstName))
                  : getFieldValue(p2FirstName)
              }
              isOverridden={!isEditMode && isFieldOverridden(p2FirstName)}
              originalValue={getOriginalValue(p2FirstName)}
              isEditing={isEditMode}
              onValueChange={v => updateField('p2_invoice_first_name', v)}
              {...restoreFieldProps('p2_invoice_first_name', 'P2 Voornaam')}
            />
            <FieldRow
              label="Achternaam"
              value={
                isEditMode
                  ? String(editedOverrides.p2_invoice_last_name || getFieldValue(p2LastName))
                  : getFieldValue(p2LastName)
              }
              isOverridden={!isEditMode && isFieldOverridden(p2LastName)}
              originalValue={getOriginalValue(p2LastName)}
              isEditing={isEditMode}
              onValueChange={v => updateField('p2_invoice_last_name', v)}
              {...restoreFieldProps('p2_invoice_last_name', 'P2 Achternaam')}
            />
            <FieldRow
              label="E-mailadres"
              value={getFieldValue(p2PrimaryEmail) || getFieldValue(p2Email)}
              isOverridden={!isEditMode && (isFieldOverridden(p2PrimaryEmail) || isFieldOverridden(p2Email))}
              originalValue={getOriginalValue(p2PrimaryEmail) || getOriginalValue(p2Email)}
              isEditing={isEditMode}
              onValueChange={v => updateField('p2_email', v)}
              {...restoreFieldProps('p2_email', 'P2 E-mailadres')}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Factuur 1{isEditMode && <Edit2 className="h-4 w-4 text-muted-foreground" />}
          </CardTitle>
          <CardDescription>Facturatiegegevens voor persoon 1</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FieldRow
            label="Bedrijfsnaam"
            value={
              isEditMode ? String(editedOverrides.invoice_company || getFieldValue(company)) : getFieldValue(company)
            }
            isOverridden={!isEditMode && isFieldOverridden(company)}
            originalValue={getOriginalValue(company)}
            isEditing={isEditMode}
            onValueChange={v => updateField('invoice_company', v)}
            {...restoreFieldProps('invoice_company')}
          />
          <FieldRow
            label="Straatnaam"
            value={isEditMode ? String(editedOverrides.invoice_street || getFieldValue(street)) : getFieldValue(street)}
            isOverridden={!isEditMode && isFieldOverridden(street)}
            originalValue={getOriginalValue(street)}
            isEditing={isEditMode}
            onValueChange={v => updateField('invoice_street', v)}
            {...restoreFieldProps('invoice_street')}
          />
          <FieldRow
            label="BTW-nummer"
            value={
              isEditMode
                ? String(editedOverrides.invoice_vat_number || getFieldValue(vatNumber))
                : getFieldValue(vatNumber)
            }
            isOverridden={!isEditMode && isFieldOverridden(vatNumber)}
            originalValue={getOriginalValue(vatNumber)}
            isEditing={isEditMode}
            onValueChange={v => updateField('invoice_vat_number', v)}
            {...restoreFieldProps('invoice_vat_number')}
          />
          <FieldRow
            label="Huisnummer"
            value={
              isEditMode
                ? String(editedOverrides.invoice_house_number || getFieldValue(houseNumber))
                : getFieldValue(houseNumber)
            }
            isOverridden={!isEditMode && isFieldOverridden(houseNumber)}
            originalValue={getOriginalValue(houseNumber)}
            isEditing={isEditMode}
            onValueChange={v => updateField('invoice_house_number', v)}
            {...restoreFieldProps('invoice_house_number')}
          />
          <FieldRow
            label="BTW-plichtig"
            type="boolean"
            value={
              isEditMode
                ? String(editedOverrides.invoice_vat_liable ?? getFieldValue(vatLiable))
                : getFieldValue(vatLiable)
            }
            isOverridden={!isEditMode && isFieldOverridden(vatLiable)}
            originalValue={getOriginalValue(vatLiable)}
            isEditing={isEditMode}
            onValueChange={v => updateField('invoice_vat_liable', v)}
            {...restoreFieldProps('invoice_vat_liable')}
          />
          <FieldRow
            label="Postcode"
            value={isEditMode ? String(editedOverrides.invoice_zip || getFieldValue(zip)) : getFieldValue(zip)}
            isOverridden={!isEditMode && isFieldOverridden(zip)}
            originalValue={getOriginalValue(zip)}
            isEditing={isEditMode}
            onValueChange={v => updateField('invoice_zip', v)}
            {...restoreFieldProps('invoice_zip')}
          />
          <FieldRow
            label="Land"
            value={
              isEditMode ? String(editedOverrides.invoice_country || getFieldValue(country)) : getFieldValue(country)
            }
            isOverridden={!isEditMode && isFieldOverridden(country)}
            originalValue={getOriginalValue(country)}
            isEditing={isEditMode}
            onValueChange={v => updateField('invoice_country', v)}
            {...restoreFieldProps('invoice_country')}
          />
          <FieldRow
            label="Stad"
            value={isEditMode ? String(editedOverrides.invoice_city || getFieldValue(city)) : getFieldValue(city)}
            isOverridden={!isEditMode && isFieldOverridden(city)}
            originalValue={getOriginalValue(city)}
            isEditing={isEditMode}
            onValueChange={v => updateField('invoice_city', v)}
            {...restoreFieldProps('invoice_city')}
          />
        </CardContent>
      </Card>

      {showSecondInvoice && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Factuur 2{isEditMode && <Edit2 className="h-4 w-4 text-muted-foreground" />}
            </CardTitle>
            <CardDescription>Facturatiegegevens voor persoon 2</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FieldRow
              label="Bedrijfsnaam"
              value={
                isEditMode
                  ? String(editedOverrides.p2_invoice_company || getFieldValue(p2Company))
                  : getFieldValue(p2Company)
              }
              isOverridden={!isEditMode && isFieldOverridden(p2Company)}
              originalValue={getOriginalValue(p2Company)}
              isEditing={isEditMode}
              onValueChange={v => updateField('p2_invoice_company', v)}
              {...restoreFieldProps('p2_invoice_company')}
            />
            <FieldRow
              label="Straatnaam"
              value={
                isEditMode
                  ? String(editedOverrides.p2_invoice_street || getFieldValue(p2Street))
                  : getFieldValue(p2Street)
              }
              isOverridden={!isEditMode && isFieldOverridden(p2Street)}
              originalValue={getOriginalValue(p2Street)}
              isEditing={isEditMode}
              onValueChange={v => updateField('p2_invoice_street', v)}
              {...restoreFieldProps('p2_invoice_street')}
            />
            <FieldRow
              label="BTW-nummer"
              value={
                isEditMode
                  ? String(editedOverrides.p2_invoice_vat_number || getFieldValue(p2VatNumber))
                  : getFieldValue(p2VatNumber)
              }
              isOverridden={!isEditMode && isFieldOverridden(p2VatNumber)}
              originalValue={getOriginalValue(p2VatNumber)}
              isEditing={isEditMode}
              onValueChange={v => updateField('p2_invoice_vat_number', v)}
              {...restoreFieldProps('p2_invoice_vat_number')}
            />
            <FieldRow
              label="Huisnummer"
              value={
                isEditMode
                  ? String(editedOverrides.p2_invoice_house_number || getFieldValue(p2HouseNumber))
                  : getFieldValue(p2HouseNumber)
              }
              isOverridden={!isEditMode && isFieldOverridden(p2HouseNumber)}
              originalValue={getOriginalValue(p2HouseNumber)}
              isEditing={isEditMode}
              onValueChange={v => updateField('p2_invoice_house_number', v)}
              {...restoreFieldProps('p2_invoice_house_number')}
            />
            <FieldRow
              label="BTW-plichtig"
              type="boolean"
              value={
                isEditMode
                  ? String(editedOverrides.p2_invoice_vat_liable ?? getFieldValue(p2VatLiable))
                  : getFieldValue(p2VatLiable)
              }
              isOverridden={!isEditMode && isFieldOverridden(p2VatLiable)}
              originalValue={getOriginalValue(p2VatLiable)}
              isEditing={isEditMode}
              onValueChange={v => updateField('p2_invoice_vat_liable', v)}
              {...restoreFieldProps('p2_invoice_vat_liable')}
            />
            <FieldRow
              label="Postcode"
              value={isEditMode ? String(editedOverrides.p2_invoice_zip || getFieldValue(p2Zip)) : getFieldValue(p2Zip)}
              isOverridden={!isEditMode && isFieldOverridden(p2Zip)}
              originalValue={getOriginalValue(p2Zip)}
              isEditing={isEditMode}
              onValueChange={v => updateField('p2_invoice_zip', v)}
              {...restoreFieldProps('p2_invoice_zip')}
            />
            <FieldRow
              label="Land"
              value={
                isEditMode
                  ? String(editedOverrides.p2_invoice_country || getFieldValue(p2Country))
                  : getFieldValue(p2Country)
              }
              isOverridden={!isEditMode && isFieldOverridden(p2Country)}
              originalValue={getOriginalValue(p2Country)}
              isEditing={isEditMode}
              onValueChange={v => updateField('p2_invoice_country', v)}
              {...restoreFieldProps('p2_invoice_country')}
            />
            <FieldRow
              label="Stad"
              value={
                isEditMode ? String(editedOverrides.p2_invoice_city || getFieldValue(p2City)) : getFieldValue(p2City)
              }
              isOverridden={!isEditMode && isFieldOverridden(p2City)}
              originalValue={getOriginalValue(p2City)}
              isEditing={isEditMode}
              onValueChange={v => updateField('p2_invoice_city', v)}
              {...restoreFieldProps('p2_invoice_city')}
            />
            <FieldRow
              label="Factuur e-mailadres"
              value={
                isEditMode ? String(editedOverrides.p2_invoice_email || getFieldValue(p2Email)) : getFieldValue(p2Email)
              }
              isOverridden={!isEditMode && isFieldOverridden(p2Email)}
              originalValue={getOriginalValue(p2Email)}
              isEditing={isEditMode}
              onValueChange={v => updateField('p2_invoice_email', v)}
              {...restoreFieldProps('p2_invoice_email')}
            />
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Calculatiegegevens
            {isEditMode && <Edit2 className="h-4 w-4 text-muted-foreground" />}
          </CardTitle>
          <CardDescription>Aantal deelnemers, supplementen en activiteiten</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <FieldRow label="Aantal deelnemers" value={String(displayParticipantCount)} isEditing={false} />
              {!isRegistrationClosed && !registration.cancelledAt && !isEditMode && displayParticipantCount <= 2 && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setParticipantCountTarget(displayParticipantCount === 1 ? 2 : 1)}
                >
                  {displayParticipantCount === 1 ? 'Deelnemer toevoegen' : 'Aantal deelnemers wijzigen'}
                </Button>
              )}
            </div>
            <FieldRow
              label="Single supplement"
              type="boolean"
              value={
                isEditMode
                  ? String(editedOverrides.calc_single_room ?? getFieldValue(singleRoom))
                  : getFieldValue(singleRoom)
              }
              isOverridden={!isEditMode && isFieldOverridden(singleRoom)}
              originalValue={getOriginalValue(singleRoom)}
              isEditing={isEditMode}
              onValueChange={v => updateField('calc_single_room', v)}
              {...restoreFieldProps('calc_single_room')}
            />
            <div className="space-y-1">
              <Label className="text-sm text-muted-foreground flex items-center gap-2">
                Verzekering
                {!isEditMode && isFieldOverridden(insurance) && (
                  <Badge variant="outline" className="text-xs">
                    Aangepast
                  </Badge>
                )}
                {restoreFieldProps('calc_insurance').onRestore && (
                  <RestoreFieldButton
                    label="Verzekering"
                    onRestore={restoreFieldProps('calc_insurance').onRestore!}
                    disabled={!canRestoreField}
                  />
                )}
              </Label>
              {isEditMode ? (
                <Select
                  value={String(editedOverrides.calc_insurance ?? getFieldValue(insurance) ?? '') || '_empty'}
                  onValueChange={v => {
                    const code = v === '_empty' ? '' : v;
                    setEditedOverrides(prev => ({
                      ...prev,
                      calc_insurance: code,
                      calc_insurance_uuid: '',
                      calc_insurance_name: '',
                    }));
                  }}
                >
                  <SelectTrigger className="font-medium">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_empty">-</SelectItem>
                    {insuranceOptions.map(option => (
                      <SelectItem key={option.code} value={option.code}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className={`font-medium ${!isEditMode && isFieldOverridden(insurance) ? 'text-primary' : ''}`}>
                  {(() => {
                    const rawCode = getFieldValue(insurance);
                    const normalized = rawCode?.trim().toLowerCase();
                    if (
                      !normalized ||
                      normalized === '-' ||
                      normalized === '—' ||
                      normalized === 'n/a' ||
                      normalized === 'na'
                    )
                      return '-';
                    const enrichedName = getFieldValue(getField('calc_insurance_name'));
                    if (enrichedName && enrichedName.trim() && enrichedName.trim() !== '-')
                      return `${enrichedName.trim()} (${rawCode})`;
                    const lookedUp = insuranceNameByCode.get(rawCode);
                    return lookedUp ? `${lookedUp} (${rawCode})` : rawCode;
                  })()}
                </p>
              )}
              {!isEditMode && isFieldOverridden(insurance) && getOriginalValue(insurance) && (
                <p className="text-xs text-muted-foreground line-through">Origineel: {getOriginalValue(insurance)}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4 rounded-lg border p-4">
              <h3 className="font-semibold">Persoon 1</h3>
              {person1Activities.length === 0 ? (
                <p className="text-sm text-muted-foreground">Geen activiteiten gekozen</p>
              ) : (
                person1Activities.map(activity => (
                  <div key={activity.key} className="space-y-1">
                    <Label className="text-sm text-muted-foreground flex items-center gap-2">
                      {activity.label}
                      {activity.isOverridden && (
                        <Badge variant="outline" className="text-xs">
                          Aangepast
                        </Badge>
                      )}
                      {restoreFieldProps(activity.key).onRestore && (
                        <RestoreFieldButton
                          label={SUBMISSION_FIELD_LABELS[activity.key]}
                          onRestore={restoreFieldProps(activity.key).onRestore!}
                          disabled={!canRestoreField}
                        />
                      )}
                    </Label>
                    {isEditMode ? (
                      <Select
                        value={activity.activityValue || '_empty'}
                        onValueChange={v => updateActivityField(activity.key, v === '_empty' ? '' : v)}
                      >
                        <SelectTrigger className="font-medium">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_empty">-</SelectItem>
                          {activityOptions.map(option => (
                            <SelectItem key={`${activity.key}-${option.code}`} value={option.code}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className={`font-medium ${activity.isOverridden ? 'text-primary' : ''}`}>{activity.value}</p>
                    )}
                    {activity.isOverridden && activity.originalValue && !isEditMode && (
                      <p className="text-xs text-muted-foreground line-through">Origineel: {activity.originalValue}</p>
                    )}
                  </div>
                ))
              )}
            </div>

            <div className="space-y-4 rounded-lg border p-4">
              <h3 className="font-semibold">Persoon 2</h3>
              {person2Activities.length === 0 ? (
                <p className="text-sm text-muted-foreground">Geen activiteiten gekozen</p>
              ) : (
                person2Activities.map(activity => (
                  <div key={activity.key} className="space-y-1">
                    <Label className="text-sm text-muted-foreground flex items-center gap-2">
                      {activity.label}
                      {activity.isOverridden && (
                        <Badge variant="outline" className="text-xs">
                          Aangepast
                        </Badge>
                      )}
                      {restoreFieldProps(activity.key).onRestore && (
                        <RestoreFieldButton
                          label={SUBMISSION_FIELD_LABELS[activity.key]}
                          onRestore={restoreFieldProps(activity.key).onRestore!}
                          disabled={!canRestoreField}
                        />
                      )}
                    </Label>
                    {isEditMode ? (
                      <Select
                        value={activity.activityValue || '_empty'}
                        onValueChange={v => updateActivityField(activity.key, v === '_empty' ? '' : v)}
                      >
                        <SelectTrigger className="font-medium">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_empty">-</SelectItem>
                          {activityOptions.map(option => (
                            <SelectItem key={`${activity.key}-${option.code}`} value={option.code}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className={`font-medium ${activity.isOverridden ? 'text-primary' : ''}`}>{activity.value}</p>
                    )}
                    {activity.isOverridden && activity.originalValue && !isEditMode && (
                      <p className="text-xs text-muted-foreground line-through">Origineel: {activity.originalValue}</p>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Payments and invoice coverage */}
      <Card>
        <CardHeader>
          <CardTitle>Betalingen & Facturatie</CardTitle>
          <CardDescription>
            Per betaling: betaalstatus en of er al een bijbehorende voorschotfactuur is aangemaakt
          </CardDescription>
        </CardHeader>
        <CardContent>
          {registration.payments.length === 0 ? (
            <p className="text-muted-foreground text-sm">Nog geen betalingen gevonden</p>
          ) : (
            <div className="space-y-3">
              {registration.payments.map(payment => {
                const linkedInvoice = paymentInvoiceMap.get(payment.id);
                return (
                  <div key={payment.id} className="border rounded-lg p-3 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <p className="font-medium">Persoon {payment.personNumber}</p>
                        <p className="text-xs text-muted-foreground">{formatDateTime(payment.createdAt)}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {payment.mollieMode === 'test' && <Badge variant="warning">TEST</Badge>}
                        <Badge variant={paymentStatusColors[payment.status]}>
                          {paymentStatusLabels[payment.status] ?? payment.status}
                        </Badge>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                      <div>
                        <p className="text-muted-foreground">Bedrag</p>
                        <p className="font-medium">{formatCurrency(payment.amount)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Mollie ID</p>
                        <p className="font-medium font-mono text-xs">{payment.molliePaymentId || '-'}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Factuurstatus</p>
                        {!DEPOSIT_INVOICE_REQUIRED_PAYMENT_STATUSES.has(payment.status) ? (
                          <Badge variant="secondary">Nog niet van toepassing</Badge>
                        ) : linkedInvoice ? (
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant="success">Factuur aangemaakt</Badge>
                            {payment.status !== 'paid' && (
                              <Badge variant="warning">Fallback na mislukte betaling</Badge>
                            )}
                            <Badge variant={invoiceStatusColors[linkedInvoice.exactStatus]}>
                              {invoiceStatusLabels[linkedInvoice.exactStatus] ?? 'Onbekend'}
                            </Badge>
                          </div>
                        ) : (
                          <Badge variant="outline">Nog geen gelinkte factuur</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Saldo bedrag</CardTitle>
            <CardDescription>
              Live berekening op basis van pakketprijs, supplementen, activiteiten en voorschot
            </CardDescription>
          </div>
          {!hasBalanceInvoice && (
            <Button
              size="sm"
              variant="outline"
              disabled={createBalanceInvoice.isPending}
              onClick={() => createBalanceInvoice.mutate({ id: registration.id })}
            >
              <FileText className="h-4 w-4 mr-2" />
              {createBalanceInvoice.isPending ? 'Bezig...' : 'Saldo-factuur aanmaken'}
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-6">
          {isInvoicePreviewLoading || !invoicePreview ? (
            <Skeleton className="h-28 w-full" />
          ) : !invoicePreview.isSplitBilling && invoicePreview.nonSplit ? (
            <div className="space-y-3">
              {invoicePreview.nonSplit.lines.map((line, index) => (
                <div key={`non-split-line-${index}`} className="flex items-start justify-between gap-4 text-sm">
                  <p className="text-muted-foreground">{line.label}</p>
                  <p className="font-medium whitespace-nowrap">{formatSignedAmount(line.amount)}</p>
                </div>
              ))}
              <div className="flex items-start justify-between gap-4 border-t pt-3">
                <p className="font-semibold">Saldo bedrag</p>
                <p className="font-semibold whitespace-nowrap">{formatSignedAmount(invoicePreview.nonSplit.total)}</p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-3 rounded-lg border p-4">
                <h3 className="font-semibold">Persoon 1</h3>
                {(invoicePreview.person1?.lines || []).map((line, index) => (
                  <div key={`p1-line-${index}`} className="flex items-start justify-between gap-4 text-sm">
                    <p className="text-muted-foreground">{line.label}</p>
                    <p className="font-medium whitespace-nowrap">{formatSignedAmount(line.amount)}</p>
                  </div>
                ))}
                <div className="flex items-start justify-between gap-4 border-t pt-3">
                  <p className="font-semibold">Totaal persoon 1</p>
                  <p className="font-semibold whitespace-nowrap">
                    {formatSignedAmount(invoicePreview.person1?.total || '0')}
                  </p>
                </div>
              </div>

              <div className="space-y-3 rounded-lg border p-4">
                <h3 className="font-semibold">Persoon 2</h3>
                {(invoicePreview.person2?.lines || []).map((line, index) => (
                  <div key={`p2-line-${index}`} className="flex items-start justify-between gap-4 text-sm">
                    <p className="text-muted-foreground">{line.label}</p>
                    <p className="font-medium whitespace-nowrap">{formatSignedAmount(line.amount)}</p>
                  </div>
                ))}
                <div className="flex items-start justify-between gap-4 border-t pt-3">
                  <p className="font-semibold">Totaal persoon 2</p>
                  <p className="font-semibold whitespace-nowrap">
                    {formatSignedAmount(invoicePreview.person2?.total || '0')}
                  </p>
                </div>
              </div>

              <div className="flex items-start justify-between gap-4 border-t pt-3 lg:col-span-2">
                <p className="font-semibold">Saldo bedrag totaal</p>
                <p className="font-semibold whitespace-nowrap">{formatSignedAmount(invoicePreview.grandTotal)}</p>
              </div>
            </div>
          )}

          <details
            className="rounded-lg border p-4"
            open={isInvoiceAdjustmentsOpen}
            onToggle={e => setIsInvoiceAdjustmentsOpen((e.currentTarget as HTMLDetailsElement).open)}
          >
            <summary className="cursor-pointer list-none font-medium flex items-center justify-between gap-3">
              <span className="inline-flex items-center gap-2">
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${isInvoiceAdjustmentsOpen ? 'rotate-180' : 'rotate-0'}`}
                />
                Factuuraanpassingen
              </span>
              <div className="inline-flex items-center gap-2">
                <Badge variant="outline">Reg {displayedAdjustments.length}</Badge>
                <Badge variant="secondary">Groep {groupInvoiceAdjustments.length}</Badge>
              </div>
            </summary>

            <div className="space-y-4 pt-4">
              {displayedAdjustments.length === 0 && groupInvoiceAdjustments.length === 0 ? (
                <p className="text-sm text-muted-foreground">Geen factuuraanpassingen ingesteld</p>
              ) : (
                <div className="space-y-2">
                  {groupInvoiceAdjustments.map((adjustment, index) => (
                    <div key={`group-invoice-adjustment-${index}`} className="rounded border p-3 bg-muted/20">
                      <div className="space-y-1 text-sm">
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary">Regel toevoegen</Badge>
                          <Badge variant="outline">Van groep</Badge>
                        </div>
                        <p className="text-muted-foreground">
                          <span className="font-medium">
                            {getLocalizedText(
                              adjustment.description,
                              (registration.language || 'nl') as 'nl' | 'fr' | 'en'
                            )}
                          </span>{' '}
                          ({adjustment.articleNumber}) • {adjustment.amount}
                        </p>
                      </div>
                    </div>
                  ))}
                  {displayedAdjustments.map((adjustment, index) => (
                    <div key={`invoice-adjustment-${index}`} className="rounded border p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="space-y-1 text-sm">
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary">
                              {adjustment.type === 'set_line_amount' ? 'Bedrag aanpassen' : 'Regel toevoegen'}
                            </Badge>
                            <Badge variant="outline">{scopeLabels[adjustment.scope]}</Badge>
                          </div>
                          {adjustment.type === 'set_line_amount' ? (
                            <p className="text-muted-foreground">
                              Artikel <span className="font-mono">{adjustment.targetArticleNumber}</span> naar{' '}
                              <span className="font-medium">{adjustment.amount}</span>
                            </p>
                          ) : (
                            <p className="text-muted-foreground">
                              <span className="font-medium">{adjustment.description}</span> ({adjustment.articleNumber})
                              • {adjustment.amount}
                            </p>
                          )}
                        </div>
                        {isEditMode && (
                          <div className="flex items-center gap-1">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditAdjustment(adjustment, index)}
                            >
                              Bewerken
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="text-destructive"
                              onClick={() => handleRemoveAdjustment(index)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {isEditMode && (
                <div className="space-y-3 border-t pt-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <Label>Type</Label>
                      <Select
                        value={adjustmentDraft.type}
                        onValueChange={value =>
                          setAdjustmentDraft(prev => ({
                            ...prev,
                            type: value as InvoiceAdjustmentDraft['type'],
                            targetArticleNumber: '',
                            articleNumber: '',
                            description: '',
                          }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="set_line_amount">Bedrag aanpassen</SelectItem>
                          <SelectItem value="add_line">Regel toevoegen</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {editedSplitBilling && (
                      <div className="space-y-1">
                        <Label>Scope</Label>
                        <Select
                          value={adjustmentDraft.scope}
                          onValueChange={value =>
                            setAdjustmentDraft(prev => ({ ...prev, scope: value as InvoiceAdjustmentScope }))
                          }
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="person1">Persoon 1</SelectItem>
                            <SelectItem value="person2">Persoon 2</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <div className="space-y-1">
                      <Label>Bedrag (+/-)</Label>
                      <Input
                        value={adjustmentDraft.amount}
                        onChange={e => setAdjustmentDraft(prev => ({ ...prev, amount: e.target.value }))}
                        placeholder="-25.00"
                      />
                    </div>
                  </div>

                  {adjustmentDraft.type === 'set_line_amount' ? (
                    <div className="space-y-1">
                      <Label>Doel artikelnummer</Label>
                      <Select
                        value={adjustmentDraft.targetArticleNumber || '_empty'}
                        onValueChange={value =>
                          setAdjustmentDraft(prev => ({
                            ...prev,
                            targetArticleNumber: value === '_empty' ? '' : value,
                          }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecteer artikelnummer" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_empty">-</SelectItem>
                          {availableTargetArticles.map(option => (
                            <SelectItem key={option.articleNumber} value={option.articleNumber}>
                              {option.label} ({option.articleNumber})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label>Artikelnummer</Label>
                        <ArticleNumberInput
                          value={adjustmentDraft.articleNumber}
                          onChange={value => setAdjustmentDraft(prev => ({ ...prev, articleNumber: value }))}
                          placeholder="EXTRA01"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label>Omschrijving</Label>
                        <Input
                          value={adjustmentDraft.description}
                          onChange={e => setAdjustmentDraft(prev => ({ ...prev, description: e.target.value }))}
                          placeholder="Manuele correctie"
                        />
                      </div>
                    </div>
                  )}

                  {!amountPattern.test(adjustmentDraft.amount.trim()) && adjustmentDraft.amount.trim().length > 0 && (
                    <p className="text-xs text-destructive">Ongeldig bedrag. Gebruik bijvoorbeeld -12,50 of 12.50.</p>
                  )}

                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleSubmitAdjustment}
                      disabled={!amountPattern.test(adjustmentDraft.amount.trim())}
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      {editingAdjustmentIndex == null ? 'Aanpassing toevoegen' : 'Aanpassing bijwerken'}
                    </Button>
                    {editingAdjustmentIndex != null && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => resetAdjustmentDraft(editedSplitBilling ? 'person1' : 'non_split')}
                      >
                        Bewerken annuleren
                      </Button>
                    )}
                    {editedInvoiceAdjustments.length > 0 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          if (!confirm('Ben je zeker dat je alle aanpassingen wil resetten?')) return;
                          setEditedInvoiceAdjustments(
                            (registration.invoiceAdjustments as InvoiceAdjustment[] | null) ?? []
                          );
                        }}
                      >
                        Reset
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </details>
        </CardContent>
      </Card>

      {/* Invoices */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Facturen</CardTitle>
            <CardDescription>Gegenereerde facturen en Exact Online synchronisatie</CardDescription>
          </div>
          {canRegenerateDepositInvoices && (
            <Button
              size="sm"
              variant="outline"
              disabled={isRegistrationClosed || regenerateDepositInvoices.isPending}
              onClick={() => regenerateDepositInvoices.mutate({ id: registration.id })}
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              {regenerateDepositInvoices.isPending ? 'Bezig...' : 'Voorschot-facturen opnieuw aanmaken'}
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {registration.invoices.length === 0 ? (
            <p className="text-muted-foreground text-sm">Nog geen facturen gegenereerd</p>
          ) : (
            <div className="space-y-3">
              {registration.invoices.map(invoice => (
                <div key={invoice.id} className="border rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">
                        {invoice.recipientName}
                        {invoice.personNumber && (
                          <span className="text-muted-foreground font-normal ml-1">
                            (Persoon {invoice.personNumber})
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground">{formatDateTime(invoice.createdAt)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge>{invoiceTypeLabels[invoice.type] ?? invoice.type}</Badge>
                      <Badge
                        variant={invoiceStatusColors[invoice.exactStatus]}
                        title={
                          invoice.exactStatus === 'FAILED'
                            ? (invoice.errorMessage ?? 'Geen foutmelding beschikbaar')
                            : undefined
                        }
                      >
                        {invoiceStatusLabels[invoice.exactStatus] ?? 'Onbekend'}
                      </Badge>
                    </div>
                  </div>
                  {invoice.exactStatus === 'FAILED' && (
                    <p className="text-xs text-destructive">
                      Fout: {invoice.errorMessage ?? 'Geen foutmelding beschikbaar'}
                    </p>
                  )}
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {invoice.amountIncl && (
                      <div>
                        <p className="text-muted-foreground">Bedrag (incl. BTW)</p>
                        <p className="font-medium">{formatCurrency(invoice.amountIncl)}</p>
                      </div>
                    )}
                    {invoice.exactInvoiceNumber ? (
                      <div>
                        <p className="text-muted-foreground">Factuurnummer</p>
                        <p className="font-medium font-mono text-xs">{invoice.exactInvoiceNumber}</p>
                      </div>
                    ) : invoice.exactInvoiceId ? (
                      <div>
                        <p className="text-muted-foreground">Exact Invoice ID</p>
                        <p className="font-medium font-mono text-xs">{invoice.exactInvoiceId}</p>
                      </div>
                    ) : null}
                  </div>
                  {invoice.invoiceLines && invoice.invoiceLines.length > 0 && (
                    <div className="text-xs text-muted-foreground space-y-0.5 border-t pt-2 mt-1">
                      {invoice.invoiceLines.map((line, i) => (
                        <div key={i} className="flex justify-between gap-2">
                          <span>{line.description}</span>
                          <span className="font-mono shrink-0">{formatCurrency(String(line.unitPrice))}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {invoice.type === 'DEPOSIT' &&
                    (invoice.exactStatus === 'FAILED' || invoice.exactStatus === 'QUEUED') && (
                      <div className="border-t pt-2 mt-2 flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={isRegistrationClosed || retryDepositInvoice.isPending}
                          onClick={() =>
                            retryDepositInvoice.mutate({ registrationId: registration.id, invoiceId: invoice.id })
                          }
                        >
                          <RefreshCw className="h-3 w-3 mr-1" />
                          {retryDepositInvoice.isPending ? 'Bezig...' : 'Opnieuw proberen'}
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={isRegistrationClosed || markInvoiceManuallyResolved.isPending}
                          onClick={() => setManualResolutionInvoiceId(invoice.id)}
                        >
                          {markInvoiceManuallyResolved.isPending ? 'Bezig...' : 'Handmatig markeren'}
                        </Button>
                      </div>
                    )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Submission Diff – possible corrections from later submissions */}
      {submissionDiff.length > 0 && (
        <Card className="border-amber-300 bg-amber-50 dark:bg-amber-950/20">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <GitCompare className="h-4 w-4 text-amber-600" />
                <CardTitle className="text-base text-amber-800 dark:text-amber-400">Mogelijke correcties</CardTitle>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => setSelectedDiffFields(new Set(submissionDiff.map(d => d.key)))}
                >
                  Alles selecteren
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => setSelectedDiffFields(new Set())}
                >
                  Alles deselecteren
                </Button>
              </div>
            </div>
            <CardDescription className="text-amber-700 dark:text-amber-500">
              Een latere indiening bevat afwijkende waarden. Selecteer de te overnemen velden en klik op "Toepassen" —
              dit werkt als een handmatige aanpassing (Bewerken modus).
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="rounded-md border border-amber-200 bg-white dark:bg-background overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-amber-200 bg-amber-100/50 dark:bg-amber-900/20">
                    <th className="w-8 px-3 py-2" />
                    <th className="px-3 py-2 text-left font-medium text-muted-foreground">Veld</th>
                    <th className="px-3 py-2 text-left font-medium text-muted-foreground">Huidige waarde</th>
                    <th className="px-3 py-2 text-left font-medium text-muted-foreground">Laatste indiening</th>
                  </tr>
                </thead>
                <tbody>
                  {submissionDiff.map((diff, i) => (
                    <tr
                      key={diff.key}
                      className={`border-b border-amber-100 last:border-0 ${i % 2 === 1 ? 'bg-amber-50/50 dark:bg-amber-900/10' : ''}`}
                    >
                      <td className="px-3 py-2">
                        <Checkbox
                          id={`diff-${diff.key}`}
                          checked={activeSelectedDiffFields.has(diff.key)}
                          onCheckedChange={checked =>
                            setSelectedDiffFields(current => {
                              const next = new Set(current ?? activeSelectedDiffFields);
                              if (checked) next.add(diff.key);
                              else next.delete(diff.key);
                              return next;
                            })
                          }
                        />
                      </td>
                      <td className="px-3 py-2">
                        <label htmlFor={`diff-${diff.key}`} className="font-medium cursor-pointer">
                          {diff.label}
                        </label>
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">
                        {diff.currentValue || <span className="italic">leeg</span>}
                      </td>
                      <td className="px-3 py-2 font-medium text-amber-800 dark:text-amber-300">
                        {diff.latestValue || <span className="italic">leeg</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end">
              <Button
                size="sm"
                disabled={activeSelectedDiffFields.size === 0 || isRegistrationClosed || updateRegistration.isPending}
                onClick={() => void handleApplyDiff()}
              >
                <Save className="mr-2 h-3.5 w-3.5" />
                {updateRegistration.isPending ? 'Bezig...' : 'Toepassen'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Submission History */}
      <Card>
        <CardHeader>
          <CardTitle>Indiening Geschiedenis</CardTitle>
          <CardDescription>Alle formulier indieningen (audit trail)</CardDescription>
        </CardHeader>
        <CardContent>
          {registration.submissions.length === 0 ? (
            <p className="text-muted-foreground text-sm">Geen indieningen gevonden</p>
          ) : (
            <div className="space-y-2">
              {registration.submissions.map(submission => {
                const isExpanded = expandedSubmissions.has(submission.id);
                return (
                  <div key={submission.id} className="border-b pb-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">{submission.externalSubmissionId}</p>
                        <p className="text-xs text-muted-foreground">{formatDateTime(submission.createdAt)}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground"
                          title="Raw payload bekijken"
                          onClick={() =>
                            setExpandedSubmissions(prev => {
                              const next = new Set(prev);
                              if (next.has(submission.id)) next.delete(submission.id);
                              else next.add(submission.id);
                              return next;
                            })
                          }
                        >
                          <Code className="h-3.5 w-3.5" />
                        </Button>
                        <Badge variant={submission.submissionType === 'initial' ? 'default' : 'secondary'}>
                          {submission.submissionType === 'initial' ? 'Initieel' : 'Wijziging'}
                        </Badge>
                      </div>
                    </div>
                    {isExpanded && (
                      <pre className="mt-2 overflow-x-auto max-w-full rounded-md bg-muted p-3 text-xs whitespace-pre-wrap break-all">
                        <code>{JSON.stringify(submission.rawPayload, null, 2)}</code>
                      </pre>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
